import {
  Box,
  Heading,
  Text,
  Badge,
  Button,
  Flex,
  Link,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import auth from '../../../../apis/auth';
import AdminContent from '../../components/AdminContent';
import WhitelistList from './components/whitelist-list';
import { Whitelist } from './models/whitelist';
import { ENV } from '../../../../constants';

export const whitelistPageInfo = {
  title: 'Whitelist',
  description: 'Configure a whitelist usada nas requisições bridge.',
  path: 'whitelist',
};

const WhitelistTitle: FC<{ name: string }> = ({ name }) => {
  return (
    <Flex direction="column" mb="4">
      <Heading as="h1" size="md" display="flex" alignItems="center" gridGap="2">
        <Text>{name} Whitelist</Text>
        <Badge colorScheme="green">Ativo</Badge>
      </Heading>
      <Text>
        Digite os paths como regex. Para ajudar, utilize essa ferramenta:{' '}
        <Link textColor="blue.500" href="https://regexr.com/" isExternal>
          regexr.com
        </Link>
      </Text>
    </Flex>
  );
};

const WhitelistPage: FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [whitelist, setWhitelist] = useState<Whitelist>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const getWhitelist = async () => {
    if (ENV.REACT_APP_TENANT_ID !== 'default') return;
    setLoading(true);
    try {
      const response = await auth.get<Whitelist>('/vtex/whitelist');
      setWhitelist(response.data);
    } catch (error: any) {
      toast({
        title: 'Buscar whitelist',
        description: `Erro ao buscar whitelist.`,
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWhitelist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWhitelist]);

  const handleSave = async () => {
    if (!whitelist || ENV.REACT_APP_TENANT_ID !== 'default') return;
    setIsSaving(true);
    auth
      .put(`/vtex/whitelist/${whitelist.id}`, {
        provider: whitelist.provider,
        enabled: whitelist.enabled,
        config: whitelist.config,
      })
      .then(() =>
        toast({
          title: 'Atualizar Whitelist',
          description: `Sucesso. Tempo aproximado de replicação: 30s`,
          status: 'success',
          position: 'top-right',
        }),
      )
      .catch(err =>
        toast({
          title: 'Atualizar Whitelist',
          description: `Erro ao atualizar whitelist: ${err.response?.data?.message}`,
          status: 'error',
          position: 'top-right',
        }),
      )
      .finally(() => setIsSaving(false));
  };

  const handleDelete = (index: number) => {
    if (!whitelist) return;
    const { paths } = whitelist.config;
    paths.splice(index, 1);
    setWhitelist({
      ...whitelist,
      config: {
        ...whitelist.config,
        paths,
      },
    });
  };

  const handleAddPath = () => {
    if (!whitelist) return;
    const { paths } = whitelist.config;
    paths.unshift({
      enabled: false,
      isPublic: false,
      method: [],
      path: '',
    });
    setWhitelist({
      ...whitelist,
      config: {
        ...whitelist.config,
        paths,
      },
    });
  };

  return (
    <>
      <AdminContent
        title={whitelistPageInfo.title}
        description={whitelistPageInfo.description}
      >
        <Box mt="7">
          {loading && (
            <Heading as="h4" fontSize="sm">
              Carregando...
            </Heading>
          )}
          {ENV.REACT_APP_TENANT_ID !== 'default' && (
            <Text>
              A whitelist deve ser configurada em Preço Popular (default tenant)
            </Text>
          )}
          {ENV.REACT_APP_TENANT_ID === 'default' && !loading && !whitelist && (
            <Heading as="h4" fontSize="sm">
              Nenhuma whitelist encontrada
            </Heading>
          )}
          {ENV.REACT_APP_TENANT_ID === 'default' && whitelist && (
            <>
              <WhitelistTitle name={whitelist.provider.toUpperCase()} />
              <Button
                colorScheme="cyan"
                variant="outline"
                size="xs"
                onClick={handleAddPath}
                mb="2"
              >
                Adicionar Path
              </Button>
              <WhitelistList
                paths={whitelist.config.paths}
                onDelete={handleDelete}
              />
              <Flex mt="4" gridGap="2">
                <Button onClick={() => history.goBack()}>Voltar</Button>
                <Button colorScheme="blue" onClick={handleSave}>
                  <Text as="span">Salvar</Text>
                  {isSaving && (
                    <Spinner transition="0.1 all" ml="2" size="sm" />
                  )}
                </Button>
              </Flex>
            </>
          )}
        </Box>
      </AdminContent>
    </>
  );
};
export default WhitelistPage;
